/**
 * External API Tokens
 *
 * Long-lived bearer tokens used by external integrations (e.g. Power BI) to
 * authenticate against the public `/api/external/v1/...` surface. Plaintext is
 * returned ONLY on create/rotate — it is never readable again afterwards.
 *
 * CR-6543 r4: management is superadmin/staff-only. Staff mint tokens FOR a
 * customer (`company_id`), each token carrying the module scopes selected at
 * creation; the external surface rejects out-of-scope requests with 403.
 */
import api, { type AxiosResponse } from './client';

export interface ExternalApiToken {
  id: number;
  label: string;
  /** First N characters of the token (e.g. `helix_ext_3f...`) for display. */
  key_prefix: string;
  /** Module scopes this token grants (e.g. `['quality']`). */
  scopes: string[];
  company_id: number;
  company_name: string;
  created_at: string;
  created_by_name: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  is_active: boolean;
}

/**
 * The shape returned by the create + rotate endpoints — same as
 * {@link ExternalApiToken} plus the one-time plaintext `token` field.
 */
export interface ExternalApiTokenWithPlaintext extends ExternalApiToken {
  /** Plaintext token. Returned ONLY on create/rotate, never read again. */
  token: string;
}

interface ExternalApiTokenListResponse {
  count: number;
  results: ExternalApiToken[];
}

/** One selectable module scope, as served by `available-scopes/`. */
export interface AvailableScope {
  key: string;
  label: string;
}

interface AvailableScopesResponse {
  scopes: AvailableScope[];
  default: string[];
}

export const externalApiTokensAPI = {
  list: (companyId?: number): Promise<AxiosResponse<ExternalApiTokenListResponse>> =>
    api.get('/external-api-tokens/', {
      params: companyId ? { company_id: companyId } : {},
    }),

  /** Module scopes the mint dialog can offer. */
  availableScopes: (): Promise<AxiosResponse<AvailableScopesResponse>> =>
    api.get('/external-api-tokens/available-scopes/'),

  create: (
    label: string,
    expires_in_days?: number | null,
    scopes?: string[],
    company_id?: number,
  ): Promise<AxiosResponse<ExternalApiTokenWithPlaintext>> =>
    api.post('/external-api-tokens/', {
      label,
      expires_in_days,
      ...(scopes ? { scopes } : {}),
      ...(company_id ? { company_id } : {}),
    }),

  rotate: (id: number): Promise<AxiosResponse<ExternalApiTokenWithPlaintext>> =>
    api.post(`/external-api-tokens/${id}/rotate/`),

  revoke: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/external-api-tokens/${id}/`),
};
